<script setup lang="ts">
    import BaseIconButton from '@/components/BaseIconButton.vue';
    import BaseInput from '@/components/BaseInput.vue';
    import BaseToggleButton from '@/components/BaseToggleButton.vue';
    import BaseSelect from '@/components/BaseSelect.vue';
    import BaseButton from '@/components/BaseButton.vue';
    import ButtonColorPicker from '@/components/ButtonColorPicker.vue';
    import {
        updateAngle,
        updateFontColor,
        updateFontFamily,
        updateFontSize,
        updateFontStyle,
        updateFontWeight,
        updateText as updateFabricText,
        updateUnderline,
    } from '@/commons/fabricjs/FabricTextHelper';
    import { GFabricWorld } from '@/guidetti/GFabricWorld';
    import useConfigStore from '@/store/config.store';
    import checkFont from '@/utils/checkFont';
    import useProductStore from '@/store/product.store';
    import Events from '@/services/Events';
    import { loadAlreadyCreatedFields } from '@/commons/fabricjs/FabricCallbackHelper';
    import { orderElementList } from '@/commons/fabricjs/FabricPositionHelper';
    import { sortFields } from '@/utils/sortFields';
    import useField from '@/composables/useField';
    import FabricCanvasContainer from '@/components/FabricCanvasContainer.vue';
    import { onMounted, onUnmounted, useTemplateRef, computed, ref, watch } from 'vue';
    import { storeToRefs } from 'pinia';
    import { FabricImage, FabricText } from 'fabric';
    import type { ISelectOption } from '@/typescript/interfaces/ISelectOption';
    import type { ITextField } from '@/typescript/interfaces/ITextField';
    import type { IFabricWorldInitParams } from '@/commons/fabricjs/IFabricWorldInitParams';
    import type { IImageField } from '@/typescript/interfaces/IImageField';

    let fabricWorld: GFabricWorld | null;
    const fabricElement = useTemplateRef<HTMLCanvasElement | null>('fabricElement');

    const { field } = defineProps<{ field: ITextField }>();
    const fText = ref<FabricText>();
    const productStore = useProductStore();
    const { setInitialField, resetField } = useField(field.id);

    // Get already existed elements
    const { selectedProductConfiguration } = storeToRefs(productStore);
    const filteredFields = computed(() => {
        return sortFields(
            selectedProductConfiguration.value.fields.filter(
                (filteredField) =>
                    filteredField.id !== field.id &&
                    (filteredField.transform.position.x + filteredField.transform.position.y !== 0 ||
                        (filteredField as IImageField)?.url !== undefined ||
                        (filteredField as ITextField)?.defaultText !== undefined),
            ),
        );
    });

    const { fonts, fZoom, uniqueId } = storeToRefs(useConfigStore());
    const fontOptions = computed<ISelectOption<string>[]>(() => {
        return fonts.value.map((font) => ({ label: font.fontFace.family, value: font.fontFace.family, key: font.fontFace.family }));
    });

    const emit = defineEmits<{
        updateText: [string | undefined];
        close: [void];
    }>();

    const fabricParams: IFabricWorldInitParams = {
        canvas: null,
        enableRetinaScaling: true,
        includeDefaultValues: false, // do not export default values toJSON (shorter json)
        absolutePan: true,
        selection: false,
        allowTouchScrolling: true,
        allowTouchPan: true,
        uniqueId: uniqueId.value,
        preserveObjectStacking: true,
    };

    onMounted(async () => {
        fabricWorld = new GFabricWorld();
        setInitialField(field);
        fabricWorld.init({ ...fabricParams, canvas: fabricElement.value });
        fZoom.value = fabricWorld.fabricCanvas?.getZoom() ?? 1;
        loadAlreadyCreatedFields(fabricWorld, filteredFields.value, fonts.value);
        fabricWorld.addText(field.text ?? field.defaultText, field);
        const { x, y } = field.transform.position;
        const newText = fabricWorld.getFabricElementById(field.id);
        if (!(newText instanceof FabricText)) return;
        fText.value = newText;
        updateFontSize(fabricWorld.fabricCanvas, fText.value, field.fontSize);
        updateFabricText(fabricWorld.fabricCanvas, fText.value, field.text || field.defaultText, false);
        if (field.font) {
            await checkFont(field.font, fonts.value);
            const isInitialText = x + y === 0;
            updateFontFamily(fabricWorld.fabricCanvas, fText.value, field.font, isInitialText);
        }
        updateFontColor(fabricWorld.fabricCanvas, fText.value, field.color);
        updateFontWeight(fabricWorld.fabricCanvas, fText.value, field.isBold ? '700' : '300');
        updateUnderline(fabricWorld.fabricCanvas, fText.value, field.isUnderlined);
        updateFontStyle(fabricWorld.fabricCanvas, fText.value, field.isItalic ? 'italic' : 'normal');
        if (!fabricWorld) return;
        const fabricList = fabricWorld.fabricCanvas?.getObjects() as ((FabricText & { id: string }) | (FabricImage & { id: string }))[];
        const fieldOrder = selectedProductConfiguration.value.fields.map((fieldToOrder) => fieldToOrder.id);
        orderElementList(fabricWorld.fabricCanvas, fabricList, fieldOrder, undefined);
        fabricWorld.addBackgroundColor(selectedProductConfiguration.value.poleColor);
        fabricWorld.stickerAndBrand();
        fabricWorld.addGuides();
        fabricWorld.focusOnElement(field.id);
        fZoom.value = fabricWorld.fabricCanvas?.getZoom() ?? 1;
        // update text position from canvas to store
        const currentFText = fabricList.find((fElem) => fElem.id === field.id);
        if (!currentFText) return;
        updateFieldTransform({
            id: currentFText.id,
            transform: {
                angle: currentFText.angle,
                position: {
                    x: currentFText.left,
                    y: currentFText.top,
                },
                scale: {
                    x: currentFText.scaleX,
                    y: currentFText.scaleY,
                },
            },
        });
        Events.on('text-transformed', updateFieldTransform);
    });

    onUnmounted(() => {
        fabricWorld?.dispose();
        fabricWorld = null;
        Events.off('text-transformed', updateFieldTransform);
    });

    watch(fZoom, (newValue, oldValue) => {
        if (!fabricWorld) return;
        const zoom = fabricWorld.fabricCanvas?.getZoom();
        fabricWorld.zoomInOut({ zoomRef: fZoom, oldZoom: zoom ?? oldValue });
    });

    const color = computed({
        get() {
            return field.color;
        },
        set(newColor: string) {
            const fieldToEdit = { ...field };
            fieldToEdit.color = newColor;
            if (!fText.value || !fabricWorld) return;
            updateFontColor(fabricWorld.fabricCanvas, fText.value, newColor);
            productStore.updateField(fieldToEdit);
        },
    });

    const text = computed({
        get() {
            return field.text;
        },
        set(newText: string | number | null | undefined) {
            if (typeof newText === 'string' || newText === null) {
                const fieldToEdit = { ...field };
                fieldToEdit.text = newText || undefined;
                if (!fText.value || !fabricWorld) return;
                const textUpdate = updateFabricText(fabricWorld.fabricCanvas, fText.value, newText ?? field.defaultText);
                if (!textUpdate) return;
                fieldToEdit.transform.position = { x: textUpdate.left, y: textUpdate.top };
                productStore.updateField(fieldToEdit);
            }
        },
    });

    const font = computed({
        get() {
            return field.font;
        },
        async set(newFont: string) {
            await checkFont(newFont, fonts.value);
            const fieldToEdit = { ...field };
            fieldToEdit.font = newFont;
            productStore.updateField(fieldToEdit);
            if (!fText.value || !fabricWorld) return;
            updateFontFamily(fabricWorld.fabricCanvas, fText.value, newFont, false);
        },
    });

    const isBold = computed({
        get() {
            return field.isBold;
        },
        set(bold) {
            const fieldToEdit = { ...field };
            fieldToEdit.isBold = bold;
            productStore.updateField(fieldToEdit);
            if (!fText.value || !fabricWorld) return;
            updateFontWeight(fabricWorld.fabricCanvas, fText.value, bold ? '700' : '300');
        },
    });

    const isItalic = computed({
        get() {
            return field.isItalic;
        },
        set(italic) {
            const fieldToEdit = { ...field };
            fieldToEdit.isItalic = italic;
            productStore.updateField(fieldToEdit);
            if (!fText.value || !fabricWorld) return;
            updateFontStyle(fabricWorld.fabricCanvas, fText.value, italic ? 'italic' : 'normal');
        },
    });

    const isUnderlined = computed({
        get() {
            return field.isUnderlined;
        },
        set(underlined) {
            const fieldToEdit = { ...field };
            fieldToEdit.isUnderlined = underlined;
            productStore.updateField(fieldToEdit);
            if (!fText.value || !fabricWorld) return;
            updateUnderline(fabricWorld.fabricCanvas, fText.value, underlined);
        },
    });

    const fontSize = computed({
        get() {
            return field.fontSize;
        },
        set(newFontSize: string | number | null | undefined) {
            if (typeof newFontSize !== 'string' && typeof newFontSize !== 'number') {
                return;
            }
            const fieldToEdit = { ...field };
            fieldToEdit.fontSize = Number(newFontSize);
            if (!fText.value || !fabricWorld) return;
            const textUpdate = updateFontSize(fabricWorld.fabricCanvas, fText.value, fieldToEdit.fontSize);
            if (!textUpdate) return;
            fieldToEdit.transform.position = { x: textUpdate.left, y: textUpdate.top };
            productStore.updateField(fieldToEdit);
        },
    });

    const angle = computed({
        get() {
            return field.transform.angle;
        },
        set(newAngle: string | number | null | undefined) {
            if (typeof newAngle !== 'string' && typeof newAngle !== 'number') {
                return;
            }
            if (!fText.value || !fabricWorld) return;
            updateAngle(fabricWorld.fabricCanvas, fText.value, Number(newAngle));
            const fieldToEdit = { ...field };
            fieldToEdit.transform.angle = fText.value.angle;
            fieldToEdit.transform.position = { x: fText.value.left, y: fText.value.top };
            productStore.updateField(fieldToEdit);
        },
    });

    function close() {
        emit('close');
    }

    function updateText() {
        emit('updateText', field.text);
        close();
    }

    function cancel() {
        resetField();
        close();
    }

    function updateFieldTransform(fieldEdited: Partial<ITextField>) {
        const fieldToEdit = { ...field };
        if (fieldToEdit.id !== fieldEdited.id) return;
        fieldToEdit.fontSize = fieldEdited.fontSize ?? fieldToEdit.fontSize;
        fieldToEdit.transform = { ...(fieldEdited.transform ?? fieldToEdit.transform) };
        fieldToEdit.frameSize = fieldEdited.frameSize ?? fieldToEdit.frameSize;
        productStore.updateField(fieldToEdit);
    }
</script>

<template>
    <div class="flex h-full w-full flex-col items-center justify-around gap-2 bg-white p-4">
        <div class="flex w-full flex-row gap-3">
            <BaseInput
                v-model="text"
                :show-label="false"
                :placeholder="field.defaultText"
                :focus="true"
                name="text"
                :label="$t('text')"
                class="border-border h-8 grow border"
                :custom-class="[
                    field.text
                        ? 'text-text'
                        : // : 'text-red! mx-2 animate-pulse rounded-sm bg-[#007a8a] font-bold outline-none placeholder:text-white',
                          'mx-1 animate-pulse outline outline-[#007a8a]',
                ]"
            >
                <!-- :classes="[field.text ? 'bg-red' : 'text-placeholder']" -->
                <template #default="{ inputRef }">
                    <BaseIconButton
                        icon-name="PencilIcon"
                        :label="$t('edit_text')"
                        class="text-text"
                        @click="inputRef?.focus()"
                    />
                </template>
            </BaseInput>
            <ButtonColorPicker
                v-model="color"
                class="h-8 w-8 flex-none"
            />
        </div>
        <div class="flex w-full flex-row items-end gap-2">
            <BaseSelect
                v-model="font"
                :label="$t('font_select')"
                :show-label="false"
                name="font_select"
                class="grow"
                :options="fontOptions"
                :placeholder="$t('font_select_placeholder')"
            />
            <BaseInput
                v-model="fontSize"
                name="font_size"
                :label="$t('font_size')"
                :show-label="false"
                class="h-8 w-24 grow rounded-none border"
            >
                <div class="flex h-full flex-col bg-transparent">
                    <BaseIconButton
                        icon-name="ChevronUpIcon"
                        :label="$t('increase_font_size')"
                        class="h-2.5"
                        @click="fontSize++"
                    />
                    <BaseIconButton
                        icon-name="ChevronDownIcon"
                        class="h-2.5"
                        :label="$t('decrease_font_size')"
                        @click="fontSize--"
                    />
                </div>
            </BaseInput>
            <BaseToggleButton v-model="isBold">
                <span class="sr-only"> {{ $t('bold') }} </span>
                <span class="flex h-full w-full items-center justify-center font-serif text-xl font-bold"> {{ $t('bold_symbol') }} </span>
            </BaseToggleButton>
            <BaseToggleButton v-model="isItalic">
                <span class="sr-only"> {{ $t('italic') }} </span>
                <span class="flex h-full w-full items-center justify-center font-serif text-xl italic"> {{ $t('italic_symbol') }} </span>
            </BaseToggleButton>
            <BaseToggleButton v-model="isUnderlined">
                <span class="sr-only"> {{ $t('underline') }} </span>
                <span class="flex h-full w-full items-center justify-center font-serif text-xl underline">
                    {{ $t('underline_symbol') }}
                </span>
            </BaseToggleButton>
            <BaseInput
                v-model="angle"
                name="angle"
                :show-label="false"
                :label="$t('angle')"
                class="h-8 w-24 rounded-none border"
            >
                <div class="flex h-full flex-col bg-transparent">
                    <BaseIconButton
                        icon-name="ChevronUpIcon"
                        :label="$t('increase_angle')"
                        class="h-2.5"
                        @click="angle++"
                    />
                    <BaseIconButton
                        icon-name="ChevronDownIcon"
                        class="h-2.5"
                        :label="$t('decrease_angle')"
                        @click="angle--"
                    />
                </div>
                <span class="absolute -top-[3px] right-3.5 h-2">°</span>
            </BaseInput>
        </div>
        <div class="flex w-full flex-row gap-4">
            <BaseButton
                class="min-h-10 w-1/2"
                @click="cancel"
            >
                {{ $t('cancel') }}
            </BaseButton>
            <BaseButton
                color="secondary"
                class="min-h-10 w-1/2"
                variant="filled"
                @click="updateText"
            >
                {{ $t('validate') }}
            </BaseButton>
        </div>
        <FabricCanvasContainer>
            <canvas
                ref="fabricElement"
                class="h-full"
                title="texte canvas"
            />
        </FabricCanvasContainer>
    </div>
</template>
